// @ts-nocheck
import React, {
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
} from 'react';
import { Box, Grid, Typography, Popover, Chip, Button } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import MonacoEditor, { loader } from '@monaco-editor/react';

import UpdateSourceChip from '../../Tables/UpdateSourceChip';
import TextEditor from '../../ModelInventoryAndAssociation/TextEditor/TextEditor';
import { registerCustomPython } from '../../../Monaco/monacoCustomPython';
import { registerFormulaAttributeHoverProvider } from '../../../Monaco/monacoHoverProviders';
import { EMPTY_ARRAY } from '../../../../const/CommonConst';

loader.config({ paths: { vs: '/monaco' } });

const getTipTapPlainText = (node) => {
  if (!node) return '';
  if (node.type === 'text') return node.text || '';
  return (node.content || []).map(getTipTapPlainText).join(' ');
};

/**
 * AttributeInfoPopover component displays attribute information in a popover
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the popover is open
 * @param {HTMLElement} props.anchorEl - The anchor element for the popover
 * @param {Function} props.onClose - Function to handle popover close
 * @param {Object} props.attribute - The attribute data
 * @returns {React.ReactElement} The AttributeInfoPopover component
 */
const AttributeInfoPopover = ({
  open,
  anchorEl,
  onClose,
  attribute,
  attributeSourceMapping,
  loading,
  attributeList = EMPTY_ARRAY,
}) => {
  const [showAllTemplates, setShowAllTemplates] = useState(false);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const theme = useTheme();
  const { pluginConfig } = useSelector((state) => state?.tenant || {});
  const { permissions } = useSelector((state) => state?.users || {});
  const showDerivedFormula = Boolean(
    attribute?.is_derived && (!pluginConfig?.FCB || permissions?.is_super_admin)
  );
  // Ref to store the Monaco hover provider disposable
  const hoverProviderRef = useRef(null);

  const templateUsage = [];

  const attDesc =
    attribute?.attribute_origin === 'common'
      ? 'This is system generated attribute.'
      : attribute?.description;

  const plainText = useMemo(() => {
    if (!attDesc) return '';
    try {
      const parsed = JSON.parse(attDesc);
      if (parsed?.type === 'doc') {
        return getTipTapPlainText(parsed);
      }
      if (Array.isArray(parsed?.blocks)) {
        return parsed.blocks.map((b) => b.text).join('\n');
      }
    } catch {
      // If parsing fails, fallback to HTML parsing
    }
    const temp = document.createElement('div');
    temp.innerHTML = attDesc;
    return temp.textContent || attDesc;
  }, [attDesc]);

  // MAX characters before showing Read More
  const MAX_CHARS = 200;

  const fullDesc = plainText || '';
  const showReadMoreButton = fullDesc.length > MAX_CHARS;
  const displayedDesc =
    showReadMoreButton && !showFullDescription
      ? `${fullDesc.slice(0, MAX_CHARS)}...`
      : fullDesc;
  const renderedDesc = showReadMoreButton ? displayedDesc : attDesc;
  const isDescriptionEmpty = plainText.trim().length === 0;

  //Extract source details based on current attribute ID
  const sourceDetails = useMemo(() => {
    const attributeId = attribute?.id;
    return attributeSourceMapping?.[attributeId] || {};
  }, [attributeSourceMapping, attribute?.id]);

  const { source_type, template_name, automation_id, attribute_mapping_id } =
    sourceDetails;

  // Handle popover close and reset template state
  const handleClose = useCallback(() => {
    setShowAllTemplates(false);
    onClose();
  }, [onClose, setShowAllTemplates]);

  // Handle show more templates
  const handleShowMoreTemplates = useCallback(() => {
    setShowAllTemplates(true);
  }, [setShowAllTemplates]);

  const displayedTemplates = useMemo(() => {
    return showAllTemplates ? templateUsage : templateUsage.slice(0, 5);
  }, [showAllTemplates, templateUsage]);

  const hasMoreTemplates = useMemo(() => {
    return templateUsage.length > 5;
  }, [templateUsage.length]);

  const renderUpdateSourceCell = () => {
    const attributeId = attribute?.id;
    const sourceType = attributeSourceMapping?.[attributeId]?.source_type;
    if (loading) {
      return <UpdateSourceChip label="Loading..." />;
    }
    if (sourceType) {
      return <UpdateSourceChip label={sourceType} />;
    }
    return <Typography variant="body8">-</Typography>;
  };

  const handleToggleFullDescription = useCallback(() => {
    setShowFullDescription((prev) => !prev);
  }, [setShowFullDescription]);

  // Memoized handler for MonacoEditor beforeMount
  const handleEditorBeforeMount = useCallback(
    (monaco) => {
      const derivedFormulaTheme =
        theme?.palette?.monacoEditor?.derivedFormulaEditor;
      // Dispose previous hover provider to prevent duplication
      if (hoverProviderRef.current) {
        hoverProviderRef.current.dispose();
        hoverProviderRef.current = null;
      }
      registerCustomPython(monaco, {
        tokenColors: derivedFormulaTheme?.tokenColors,
        colors: derivedFormulaTheme?.colors,
      });
      monaco?.editor?.setTheme('customPythonTheme');
      // Store the disposable in the ref so we can clean up next time
      hoverProviderRef.current = registerFormulaAttributeHoverProvider(
        monaco,
        attributeList
      );
    },
    [theme, attributeList]
  );

  // Clean up hover provider on unmount
  useEffect(() => {
    return () => {
      if (hoverProviderRef.current) {
        hoverProviderRef.current.dispose();
        hoverProviderRef.current = null;
      }
    };
  }, []);

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={handleClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      slotProps={{
        paper: {
          sx: {
            maxHeight: 400,
            width: 450,
          },
        },
      }}
    >
      <Box
        component="section"
        aria-label="Attribute details"
        sx={{
          p: 2,
          maxHeight: 400,
          overflowY: 'auto',
        }}
      >
        {/* First row: Data Type and Update Source */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid
            container
            size={6}
            sx={{
              rowGap: 1,
            }}
          >
            <Grid size={12}>
              <Typography variant="body4">Data Type</Typography>
            </Grid>
            <Grid size={12}>
              <Typography variant="body8">
                {attribute?.data_type || 'Not specified'}
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            size={6}
            sx={{
              rowGap: 1,
            }}
          >
            <Grid size={12}>
              <Typography variant="body4">Update Source</Typography>
            </Grid>
            <Grid size={12}>{renderUpdateSourceCell()}</Grid>
          </Grid>
        </Grid>

        {/* Second Row: Source Template only for Calculated , Automation and Attribute Mapping Source Type and ID only for Automation and Attribute Mapping */}
        {(source_type === 'Automation' ||
          source_type === 'Mapping' ||
          source_type === 'Calculated') && (
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {/* Source Template */}
            <Grid
              container
              size={6}
              sx={{
                rowGap: 1,
              }}
            >
              <Grid size={12}>
                <Typography variant="body4">Source Template</Typography>
              </Grid>

              <Grid size={12}>
                <Typography variant="body8">{template_name || '-'}</Typography>
              </Grid>
            </Grid>

            {/* ID Column */}
            {(source_type === 'Automation' || source_type === 'Mapping') && (
              <Grid
                container
                size={6}
                sx={{
                  rowGap: 1,
                }}
              >
                <Grid size={12}>
                  <Typography variant="body4">
                    {source_type === 'Mapping' ? 'Mapping ID' : 'Automation ID'}
                  </Typography>
                </Grid>

                <Grid size={12}>
                  <Typography variant="body8">
                    {source_type === 'Mapping'
                      ? attribute_mapping_id || '-'
                      : automation_id || '-'}
                  </Typography>
                </Grid>
              </Grid>
            )}
          </Grid>
        )}

        {showDerivedFormula && (
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid
              container
              size={12}
              sx={{
                rowGap: 1,
              }}
            >
              <Grid size={12}>
                <Typography variant="body4">Derived Formula</Typography>
              </Grid>
              <Grid size={12}>
                {attribute?.derived_formula ? (
                  <Box
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                    }}
                  >
                    <MonacoEditor
                      height="150px"
                      language="custom-python"
                      theme="customPythonTheme"
                      value={attribute?.derived_formula || ''}
                      options={{
                        minimap: { enabled: false },
                        fontSize: 14,
                        readOnly: true,
                        scrollBeyondLastLine: false,
                        wordWrap: 'on',
                        lineNumbers: 'off',
                        folding: false,
                        renderLineHighlight: 'none',
                        scrollbar: {
                          vertical: 'auto',
                          horizontal: 'auto',
                        },
                      }}
                      beforeMount={handleEditorBeforeMount}
                    />
                  </Box>
                ) : (
                  <Typography variant="body8">-</Typography>
                )}
              </Grid>
            </Grid>
          </Grid>
        )}

        <Grid container spacing={2}>
          <Grid
            container
            size={12}
            sx={{
              rowGap: 1,
            }}
          >
            <Grid size={12}>
              <Typography variant="body4">Description</Typography>
            </Grid>
            <Grid size={12}>
              <Box>
                {!isDescriptionEmpty ? (
                  <TextEditor content={renderedDesc} readOnly />
                ) : (
                  <Typography>No description available.</Typography>
                )}
              </Box>
              {showReadMoreButton && (
                <Button
                  size="small"
                  onClick={handleToggleFullDescription}
                  sx={{ mt: 1 }}
                >
                  {showFullDescription ? 'Show less' : 'Read more'}
                </Button>
              )}
            </Grid>
          </Grid>
        </Grid>
        {/* Second container: Template Usage */}
        {templateUsage.length > 0 && (
          <Grid container spacing={2}>
            <Grid size={12}>
              <Typography variant="body2">Templates</Typography>
            </Grid>
            <Grid size={12}>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1,
                  mb: hasMoreTemplates && !showAllTemplates ? 1 : 0,
                }}
              >
                {displayedTemplates.map((template) => (
                  <Chip
                    key={template}
                    label={template}
                    color="primary"
                    variant="primary"
                    size="small"
                  />
                ))}
              </Box>
            </Grid>
            {hasMoreTemplates && !showAllTemplates && (
              <Grid size={12}>
                <Button size="small" onClick={handleShowMoreTemplates}>
                  Show More ({templateUsage.length - 5} more)
                </Button>
              </Grid>
            )}
          </Grid>
        )}
      </Box>
    </Popover>
  );
};

AttributeInfoPopover.propTypes = {
  open: PropTypes.bool.isRequired,
  anchorEl: PropTypes.instanceOf(Element),
  onClose: PropTypes.func.isRequired,
  attribute: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    data_type: PropTypes.string,
    attribute_origin: PropTypes.string,
    template_usage: PropTypes.arrayOf(PropTypes.string),
    description: PropTypes.string,
    is_derived: PropTypes.bool,
    derived_formula: PropTypes.string,
  }),
  attributeSourceMapping: PropTypes.objectOf(
    PropTypes.shape({
      source_type: PropTypes.string,
    })
  ),
  loading: PropTypes.bool,
  attributeList: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      label: PropTypes.string,
      type: PropTypes.string,
    })
  ),
};

export default AttributeInfoPopover;
